/**
 * Dates, through Luxon: reading the server's stamps, the app's few formats,
 * and "MM-DD" dates placed in a year.
 *
 * Luxon is re-exported from here so the app has one door to it. Two readings
 * differ on purpose: parseDate takes a stamp as local wall-clock time (a
 * calendar day, a photo's EXIF), while fromServerStamp takes the database's
 * stamps as UTC and turns them into the gardener's own time.
 *
 * A "MM-DD" date becomes a date only through dateFromMMDD (and the
 * monthDayInYear inside it), so February 29 is the 28th in a year without
 * one everywhere alike.
 *
 * Dates speak the app's language, not only the browser's: Luxon's default
 * locale follows the language chosen in Grow (onLanguageChange), so a
 * Spanish garden on an English phone reads "7 oct", not "Oct 7". Where the
 * browser already speaks that language its own locale is kept (en-CA,
 * es-MX), with its own way of writing a date.
 *
 * Which to use, by what the server sent:
 * - a moment it recorded (created_at, timestamp, invitedAt; "2026-09-16
 *   20:38:18" or ISO, always UTC): fromServerStamp, or serverStampMs to sort
 *   or compare. Never Date.parse or DateTime.fromISO without a zone.
 * - a calendar day (a photo's date_taken, a plan's freeUntil, "YYYY-MM-DD"):
 *   calendarDay. Never through UTC: the day itself is what was meant.
 * - a day in no year ("MM-DD": frost dates, planting windows, the normals):
 *   dateFromMMDD.
 * - a local wall-clock stamp (EXIF, a tray photo's taken_at): parseDate.
 * Going the other way, send a day as DateTime.local().toISODate() (the
 * gardener's own today), not as a moment (api/photos asCalendarDay, api/tray).
 *
 * Who uses it: about fifty files across the app; import DateTime from here,
 * not from 'luxon'.
 *
 * @module dateUtils
 */

import { DateTime, Settings } from 'luxon';
import { debugLog } from '@/config/config';
import { onLanguageChange } from '@/shared/i18n/i18nSetup';

/**
 * Whether the browser's own languages include this one (by its first part:
 * en-CA speaks en).
 *
 * @param language - The app's language code.
 * @returns True when the browser's locale can be left to write dates.
 */
function browserSpeaks(language: string): boolean {
  try {
    const spoken = typeof navigator !== 'undefined' ? navigator.languages ?? [navigator.language] : [];
    return spoken.some((tag) => (tag ?? '').toLowerCase().split('-')[0] === language);
  } catch {
    return false;
  }
}

// '' is Luxon's "the system's own"; anything else is a locale for every date written.
onLanguageChange((language) => {
  Settings.defaultLocale = browserSpeaks(language) ? '' : language;
});

/**
 * Get the current date as a DateTime object, in the browser's time zone.
 *
 * @returns {DateTime} Now
 */
export const getCurrentDate = (): DateTime => {
  return DateTime.local();
};

/**
 * Parse a date string into a DateTime object, as local wall-clock time (ISO
 * first, then MySQL's form). Not for the server's UTC stamps: fromServerStamp.
 *
 * @param {string} dateString - Date string to parse
 * @returns {DateTime} Parsed DateTime object
 * @throws {Error} If date string is invalid or empty
 */
export const parseDate = (dateString: string): DateTime => {
  debugLog('DateUtils', 'parseDate input:', dateString);

  if (!dateString) {
    debugLog('DateUtils', 'Warning: Empty date string provided to parseDate');
    throw new Error('Empty date string provided');
  }

  // Try ISO format first
  let parsed = DateTime.fromISO(dateString);
  
  // If not valid, try MySQL format
  if (!parsed.isValid) {
    parsed = DateTime.fromSQL(dateString);
  }

  debugLog('DateUtils', 'Parsed result:', parsed.toISO());

  if (!parsed.isValid) {
    debugLog('DateUtils', 'Warning: Invalid date string:', dateString);
    throw new Error(`Invalid date string: ${dateString}`);
  }

  return parsed;
};

/**
 * A DateTime's calendar day, "YYYY-MM-DD".
 *
 * @param {DateTime} dateTime - DateTime object to convert
 * @returns {string} The day; '' for an invalid DateTime
 */
export const toISOString = (dateTime: DateTime): string => {
  return dateTime.toISODate() ?? '';
};

/**
 * Format a date as a short month and day ("Sep 16"; "16 sept" in Spanish), in
 * Luxon's default locale, which follows the app's language (module note).
 *
 * @param {DateTime} date - Date to format
 * @param {boolean} includeYearIfDifferent - Add the year when it is not this year
 * @returns {string} Formatted date string
 */
export const formatDate = (date: DateTime, includeYearIfDifferent: boolean = false): string => {
  if (includeYearIfDifferent && date.year !== getCurrentDate().year) {
    return date.toLocaleString({ month: 'short', day: 'numeric', year: 'numeric' });
  }
  return date.toLocaleString({ month: 'short', day: 'numeric' });
};

/**
 * Parse a MM-DD string into month and day numbers (unchecked; dateFromMMDD checks).
 *
 * @param {string} mmdd - Date string in MM-DD format
 * @returns {{ month: number; day: number }} Parsed month and day
 */
const parseMMDD = (mmdd: string): { month: number; day: number } => {
  const [month, day] = mmdd.split('-').map(Number);
  return { month, day };
};

/**
 * Which parts of a date a gardener shows on their public pages and shares
 * (Profile > Public pages; the share screen's Day and Year): the month is
 * always said.
 */
export interface DateParts {
  /** The day of the month ("21"). */
  day: boolean;
  /** The year ("2024"). */
  year: boolean;
}

/** Every part shown: the pages as they always were. */
export const ALL_DATE_PARTS: DateParts = Object.freeze({ day: true, year: true });

/**
 * A date with only the parts the gardener shows (owner, 2026-10-14: "january
 * 2024 or january 21 or january 21 2024"), in the app's language.
 *
 * short, as the public pages say dates: "Jan 21", with the year only on a
 * date from another year ("Jan 21, 2024"). long, as the share picture prints
 * them: "January 21, 2026", the year always. Either way a part switched off
 * is left out, and a date without its day names the month in full ("January
 * 2024", "January").
 *
 * @param date - The date.
 * @param parts - Which parts to say.
 * @param style - 'short' (the pages) or 'long' (the share picture).
 * @returns The date in words.
 */
export const formatDateParts = (date: DateTime, parts: DateParts, style: 'short' | 'long'): string => {
  const year = parts.year && (style === 'long' || date.year !== getCurrentDate().year);
  return date.toLocaleString({
    month: parts.day && style === 'short' ? 'short' : 'long',
    ...(parts.day ? { day: 'numeric' as const } : {}),
    ...(year ? { year: 'numeric' as const } : {}),
  });
};

/**
 * Format a date as month, day and year in words ("September 16, 2026"), in
 * the app's language.
 *
 * @param {DateTime} date - Date to format
 * @returns {string} Formatted long date string
 */
export const formatDateLong = (date: DateTime): string => {
  return date.toLocaleString({ month: 'long', day: 'numeric', year: 'numeric' });
};

/**
 * A month and day in a given year, with February 29 falling back to
 * February 28 in a year that has no 29th.
 *
 * The one door every "MM-DD" date (frost dates, season dates, the normals'
 * stretches) goes through on its way to being a date. A frost date of 02-29
 * used to come out as an invalid date three years in four: "Invalid
 * DateTime" on Today and Seasons, and no countdown at all.
 *
 * @param month - Month, 1-12.
 * @param day - Day of the month.
 * @param year - The year to place it in; this year unless given.
 * @returns The day at local midnight, or null for a day no year has (month 13, Feb 30).
 */
const monthDayInYear = (month: number, day: number, year: number = DateTime.local().year): DateTime | null => {
  if (!Number.isFinite(month) || !Number.isFinite(day) || !Number.isFinite(year)) return null;
  const fallsBack = month === 2 && day === 29 && !DateTime.fromObject({ year }).isInLeapYear;
  const date = DateTime.fromObject({ year, month, day: fallsBack ? 28 : day });
  // Luxon answers an impossible day (month 0, Feb 30) with an invalid
  // DateTime rather than throwing; null is what this promises for it.
  return date.isValid ? date : null;
};

/**
 * A "MM-DD" date in a given year (monthDayInYear), February 29 falling back
 * to the 28th in a year without one.
 *
 * @param mmdd - The date, "MM-DD", or nothing.
 * @param year - The year to place it in; this year unless given.
 * @returns The day at local midnight, or null when there is none or it cannot be read.
 */
export const dateFromMMDD = (mmdd: string | null | undefined, year?: number): DateTime | null => {
  if (!mmdd || !/^\d{1,2}-\d{1,2}$/.test(mmdd.trim())) return null;
  const { month, day } = parseMMDD(mmdd.trim());
  return monthDayInYear(month, day, year);
};

/**
 * A moment the server recorded, in the gardener's own time.
 *
 * The database hands back its own "2026-09-16 20:38:18" (UTC, without the T
 * or the zone) as well as ISO stamps, and fromISO calls the first invalid:
 * the photo tray labelled plants "Invalid DateTime", and the season page left
 * out the day a plant was added.
 *
 * @param value - An ISO or MySQL stamp, or nothing.
 * @returns The moment in local time, or null when there is none or it cannot be read.
 */
export const fromServerStamp = (value: string | null | undefined): DateTime | null => {
  if (!value) return null;
  let parsed = DateTime.fromISO(value, { zone: 'utc' });
  if (!parsed.isValid) parsed = DateTime.fromSQL(value, { zone: 'utc' });
  return parsed.isValid ? parsed.toLocal() : null;
};

/**
 * The calendar day a date says, as a local day: a photo's date_taken
 * ("2026-09-16", or an ISO stamp beginning with that day). Only the day is
 * read, so no time zone can move it. Converting to UTC first, as three places
 * did, put a gardener east of Greenwich on the day before: the API sends the
 * day itself, not midnight UTC.
 *
 * @param value - The date as the API sends it.
 * @returns That day at local midnight (invalid when the text is not a date).
 */
export const calendarDay = (value: string): DateTime => DateTime.fromISO(String(value).slice(0, 10));

/**
 * A moment the server recorded, as milliseconds since the epoch, for sorting
 * and comparing. Date.parse is no substitute: it reads the database's
 * "2026-09-16 20:38:18" as local time in Chrome (hours off) and not at all in
 * Safari, so every iPhone saw new plants as undated.
 *
 * @param value - An ISO or MySQL stamp, or a YYYY-MM-DD day (read as UTC midnight, as Date.parse does).
 * @returns Milliseconds, or NaN when there is none or it cannot be read.
 */
export const serverStampMs = (value: string | null | undefined): number => (
  fromServerStamp(value)?.toMillis() ?? Number.NaN
);

/** Luxon's DateTime, re-exported so the app imports dates from one place. */
export { DateTime }; 